import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import styled from '@emotion/styled';
import { showAlert } from '../redux/slices/alerts';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { useBrandName } from '../utils/brandUtils';
import {
    isPushSupported,
    getPermission,
    fetchVapidPublicKey,
    subscribeToPush,
    unsubscribeFromPush,
    getCurrentSubscription,
} from '../utils/pushSubscription';

const PromptRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
}));

const PromptText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    minWidth: 0,
}));

const PromptButton = styled(Button)({
    flexShrink: 0,
});

export const usePushSubscription = () => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const pushRefreshTrigger = useSelector((state) => state.refreshTriggers.push);
    const [busy, setBusy] = useState(false);
    const [state, setState] = useState({
        loading: true,
        supported: true,
        serverEnabled: true,
        permission: 'default',
        subscribed: false,
    });

    const refresh = useCallback(async () => {
        if (!isPushSupported()) {
            setState({ loading: false, supported: false, serverEnabled: true, permission: 'unsupported', subscribed: false });
            return;
        }
        let serverEnabled = false;
        try {
            serverEnabled = !!(await fetchVapidPublicKey(dispatch));
        } catch {
            serverEnabled = false;
        }
        let subscription = null;
        try {
            subscription = await getCurrentSubscription();
        } catch {
            subscription = null;
        }
        setState({ loading: false, supported: true, serverEnabled, permission: getPermission(), subscribed: !!subscription });
    }, [dispatch]);

    useEffect(() => {
        refresh();
    }, [refresh, pushRefreshTrigger]);

    const enable = useCallback(async () => {
        setBusy(true);
        try {
            await subscribeToPush(dispatch);
            dispatch(showAlert({ severity: 'success', message: 'Push notifications are on for this device.' }));
        } catch (err) {
            dispatch(showAlert({ severity: 'error', message: err.message || 'Could not turn on push notifications.' }));
        } finally {
            await refresh();
            setBusy(false);
            dispatch(triggerRefresh({ triggerName: 'push' }));
        }
    }, [dispatch, refresh]);

    const disable = useCallback(async () => {
        setBusy(true);
        try {
            await unsubscribeFromPush(dispatch);
            dispatch(showAlert({ severity: 'info', message: 'Push notifications are off for this device.' }));
        } catch (err) {
            dispatch(showAlert({ severity: 'error', message: err.message || 'Could not turn off push notifications.' }));
        } finally {
            await refresh();
            setBusy(false);
            dispatch(triggerRefresh({ triggerName: 'push' }));
        }
    }, [dispatch, refresh]);

    const { loading, supported, serverEnabled, permission, subscribed } = state;
    const blocked = !loading && supported && serverEnabled && permission === 'denied';
    const available = !loading && supported && serverEnabled && !blocked;

    let statusText;
    if (blocked) {
        statusText = 'Blocked in your browser settings for this site — allow notifications there, then reload this page.';
    } else if (subscribed) {
        statusText = 'On for this browser on this device. Every device you use needs its own switch turned on.';
    } else {
        statusText = `Off for this device. Turn it on to get alerts even when ${brandName} is closed — each device is set up separately.`;
    }

    const toggle = useCallback(
        (event) => (event.target.checked ? enable() : disable()),
        [enable, disable],
    );

    return { busy, subscribed, available, blocked, statusText, enable, disable, toggle };
};

const PushNotificationToggle = () => {
    const { available, subscribed, busy, enable } = usePushSubscription();

    if (!available || subscribed) return null;

    return (
        <PromptRow>
            <PromptText variant="caption">Get these alerts on your device.</PromptText>
            <PromptButton size="small" onClick={enable} disabled={busy}>
                {busy ? <CircularProgress size={14} /> : 'Turn on'}
            </PromptButton>
        </PromptRow>
    );
};

export default PushNotificationToggle;
